/** Cascata tipo → subtipo1 → subtipo2 → subtipo3 (níveis posteriores sobrescrevem). */

export const SUBTYPE_SCOPE_BY_LEVEL = {
  1: "subtipo1",
  2: "subtipo2",
  3: "subtipo3",
};

export function mergeTipoConfigChain(nodes = []) {
  const merged = {
    produtos_padrao: null,
    campanhas_padrao: null,
    ocultar_produto: 0,
    ocultar_campanha: 0,
    ocultar_dataadicional: 0,
    ocultar_textoadicional: 0,
    allowed_positions: [],
    source_urls: [],
    documents: [],
  };

  for (const node of nodes.filter(Boolean)) {
    if (node.produtos_padrao != null && String(node.produtos_padrao).trim() !== "") {
      merged.produtos_padrao = node.produtos_padrao;
    }
    if (node.campanhas_padrao != null && String(node.campanhas_padrao).trim() !== "") {
      merged.campanhas_padrao = node.campanhas_padrao;
    }
    if (node.ocultar_produto != null) {
      merged.ocultar_produto = Number(node.ocultar_produto) === 1 ? 1 : 0;
    }
    if (node.ocultar_campanha != null) {
      merged.ocultar_campanha = Number(node.ocultar_campanha) === 1 ? 1 : 0;
    }
    if (node.ocultar_dataadicional != null) {
      merged.ocultar_dataadicional = Number(node.ocultar_dataadicional) === 1 ? 1 : 0;
    }
    if (node.ocultar_textoadicional != null) {
      merged.ocultar_textoadicional = Number(node.ocultar_textoadicional) === 1 ? 1 : 0;
    }
    if (Array.isArray(node.allowed_positions) && node.allowed_positions.length > 0) {
      merged.allowed_positions = [...node.allowed_positions];
    }
    if (Array.isArray(node.source_urls) && node.source_urls.length > 0) {
      merged.source_urls = [...node.source_urls];
    }
    if (Array.isArray(node.documents) && node.documents.length > 0) {
      merged.documents = [...node.documents];
    }
  }

  return merged;
}

/**
 * Monta contexto de edição de subtipo a partir da árvore carregada (GET /types?tree=1).
 */
export function findSubtypeEditContext(types, { level, sub, typeId, sub1Id, sub2Id }) {
  const root = (types || []).find((t) => String(t.id) === String(typeId));
  if (!root || !sub) return null;

  const chain = [root];

  if (level >= 1) {
    const s1 = (root.children || []).find((c) =>
      String(c.id) === String(level === 1 ? sub.id : sub1Id)
    );
    if (!s1) return null;
    chain.push(s1);

    if (level >= 2) {
      const s2 = (s1.children || []).find((c) =>
        String(c.id) === String(level === 2 ? sub.id : sub2Id)
      );
      if (!s2) return null;
      chain.push(s2);

      if (level >= 3) {
        const s3 = (s2.children || []).find((c) => String(c.id) === String(sub.id));
        if (!s3) return null;
        chain.push(s3);
      }
    }
  }

  const inherited = mergeTipoConfigChain(chain.slice(0, -1));
  const parentLabel =
    chain.length > 1 ? chain[chain.length - 2]?.nome : root.nome;
  const node = chain[chain.length - 1] || {};
  const extra = Object.fromEntries(
    Object.entries(sub || {}).filter(([, value]) => value != null && value !== "")
  );

  return {
    rootType: root,
    subtypeLevel: level,
    chain,
    inherited,
    parentLabel,
    typeData: { ...node, ...extra },
  };
}

export function subtypeFormTitle(level, isNew) {
  const label = `Subtipo ${level}`;
  return isNew ? `Adicionar ${label}` : `Editar ${label}`;
}

export function isTypeActive(t) {
  if (Number(t?.excluido) === 1) return false;
  return t?.status == null || Number(t.status) === 1;
}

/** Rótulo do select: nome real + sufixo quando inativo/excluído. */
export function formatTypeOptionLabel(t) {
  const nome = String(t?.nome || "").trim();
  const label = nome || (t?.id != null ? `Tipo ${t.id}` : "");
  if (!label) return "";
  if (Number(t?.excluido) === 1) return `${label} (excluído)`;
  if (!isTypeActive(t)) return `${label} (inativo)`;
  return label;
}

/** Inclui tipos já vinculados à solicitação mesmo quando inativos/excluídos (edição). */
export function mergePinnedTypesFromConversation(typesFlat, conv) {
  if (!conv) return typesFlat || [];
  const list = typesFlat || [];
  const ids = new Set(list.map((t) => String(t.id)));
  const extras = [];

  const pin = (id, nome, id_pai, status, excluido) => {
    if (id == null || id === "" || ids.has(String(id))) return;
    const softDeleted = Number(excluido) === 1;
    const inactive =
      softDeleted || (status != null && Number(status) !== 1);
    extras.push({
      id: Number(id) || id,
      nome: String(nome || "").trim() || `Tipo ${id}`,
      id_pai: id_pai != null && id_pai !== "" ? Number(id_pai) || id_pai : null,
      status: inactive ? 0 : status != null ? Number(status) : 1,
      excluido: softDeleted ? 1 : 0,
    });
    ids.add(String(id));
  };

  pin(
    conv.typeId,
    conv.typeName || conv.tipo_nome,
    null,
    conv.typeStatus,
    conv.typeExcluido
  );
  pin(
    conv.subtype1,
    conv.subtype1Name || conv.subtipo1_nome,
    conv.typeId,
    conv.subtype1Status,
    conv.subtype1Excluido
  );
  pin(
    conv.subtype2,
    conv.subtype2Name || conv.subtipo2_nome,
    conv.subtype1,
    conv.subtype2Status,
    conv.subtype2Excluido
  );
  pin(
    conv.subtype3,
    conv.subtype3Name || conv.subtipo3_nome,
    conv.subtype2,
    conv.subtype3Status,
    conv.subtype3Excluido
  );

  return extras.length ? [...list, ...extras] : list;
}

export function buildTypePermissionSettingsPath({
  typeId,
  subtype1,
  subtype2,
  subtype3,
} = {}) {
  const params = new URLSearchParams();
  if (typeId) params.set("type", String(typeId));
  if (subtype1) params.set("sub1", String(subtype1));
  if (subtype2) params.set("sub2", String(subtype2));
  if (subtype3) params.set("sub3", String(subtype3));
  const qs = params.toString();
  return qs ? `/settings/types?${qs}` : "/settings/types";
}
